import { useEffect, useRef, useState } from 'react';
import { trackProductImpressionsWithPosition } from '@helpers/analyticsHelpers/trackProductImpressions';
import { ItemType } from '@molecules/Carousel/Carousel';
import { trackCarouselSlideShown } from '@helpers/trackCarousel';
import Foldout from './Foldout';
import useProductCarousel from './useProductCarousel';
import useCustomer from '@hooks/useCustomer';
import {
  StyledFoldoutWrapper,
  StyledGridWrapper,
  StyledSpinner,
  StyledCarousel,
} from './TwoTilesFoldoutComponent.styles';
import { AxfoodProductDetailsViewModel } from '@occ/api-client';
import useTwoTilesProducts from './useTwoTilesProducts';

const TwoTilesFoldoutComponent = ({
  data,
  show,
  title,
  onClose,
  onOpenAnimationEnd,
}: {
  data: TwoTilesComponentType;
  show: boolean;
  title: string;
  onClose: () => void;
  onOpenAnimationEnd?: () => void;
}) => {
  const { customer } = useCustomer();
  const isLoggedIn = !customer?.isAnonymous;
  const gridRef = useRef<HTMLDivElement>(null);
  const [animatingClosed, setAnimatingClosed] = useState(false);
  const isVisible = show || animatingClosed;
  const { amountOfSlides, amountOfItemsPerSlide, productElements, makeProductElements, recalculateSlides } =
    useProductCarousel(show, gridRef, data.productListTitle);
  const { showSpinner, products = [] } = useTwoTilesProducts(data.uid, show);

  useEffect(() => {
    if (products.length) makeProductElements(products);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const trackProducts = (items: ItemType[]) => {
    trackCarouselSlideShown();
    const productArray: { product: AxfoodProductDetailsViewModel; position: number }[] = [];
    items.forEach((el) => {
      productArray.push({ product: (el.item as any)?.props?.product, position: el.index });
    });

    if (productArray.length > 0) {
      trackProductImpressionsWithPosition(productArray, title, isLoggedIn);
    }
  };

  const handleAnimationEnd = () => {
    if (!show) setAnimatingClosed(false);
  };

  useEffect(() => {
    if (!show) setAnimatingClosed(true);
  }, [show]);

  return (
    <StyledFoldoutWrapper ref={gridRef} $show={isVisible}>
      <Foldout
        show={show}
        callback={recalculateSlides}
        onAnimationEnd={handleAnimationEnd}
        onOpenAnimationEnd={onOpenAnimationEnd}
      >
        <StyledGridWrapper $hasPagination={amountOfSlides > 1}>
          {showSpinner ? (
            <StyledSpinner />
          ) : (
            <StyledCarousel
              elements={productElements}
              elementsPerSlide={amountOfItemsPerSlide}
              variant="mixmatch"
              onSlideShown={trackProducts}
              title={title}
              onClose={onClose}
            />
          )}
        </StyledGridWrapper>
      </Foldout>
    </StyledFoldoutWrapper>
  );
};

export default TwoTilesFoldoutComponent;
